import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { DefaultButton, PrimaryButton, Spinner, SpinnerSize } from '@fluentui/react'

import {
  ActivityLogEvent,
  ActivityLogStats,
  activityLogReportUrl,
  getActivityLogStats,
  getActivityLogs,
} from '../../api'
import { AppStateContext } from '../../state/AppProvider'

import styles from './ActivityLogs.module.css'

const WINDOW_OPTIONS: { label: string; days: number }[] = [
  { label: '24 hours', days: 1 },
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
]

const ACTION_FILTERS: { label: string; value: string }[] = [
  { label: 'All events', value: '' },
  { label: 'Agency login', value: 'login' },
  { label: 'Agency logout', value: 'logout' },
  { label: 'Agency failed login', value: 'login_failed' },
  { label: 'Password change', value: 'password_change' },
  { label: 'Candidate register', value: 'candidate_register' },
  { label: 'Candidate login', value: 'candidate_login' },
  { label: 'Candidate failed login', value: 'candidate_login_failed' },
  { label: 'Candidate logout', value: 'candidate_logout' },
  { label: 'Candidate password reset request', value: 'candidate_password_reset_request' },
  { label: 'Candidate password reset', value: 'candidate_password_reset' },
  { label: 'Candidate resume upload', value: 'candidate_cv_upload' },
  { label: 'Candidate deregistered', value: 'candidate_deregister' },
]

const ACTION_LABELS: Record<string, string> = ACTION_FILTERS.reduce(
  (acc, opt) => {
    if (opt.value) acc[opt.value] = opt.label
    return acc
  },
  {} as Record<string, string>,
)

const formatTimestamp = (ts: string | null): string => {
  if (!ts) return ''
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ts
  return d.toLocaleString()
}

const actionLabel = (action: string): string => ACTION_LABELS[action] || action

const actionBadgeClass = (action: string): string => {
  if (action.includes('failed')) return styles.badgeError
  if (action.includes('login') || action === 'candidate_register') return styles.badgeSuccess
  if (action.includes('password')) return styles.badgeWarn
  if (action.includes('logout')) return styles.badgeNeutral
  if (action.includes('upload')) return styles.badgeInfo
  return styles.badgeNeutral
}

const ActivityLogs = () => {
  const appStateContext = useContext(AppStateContext)
  const [statsDays, setStatsDays] = useState<number>(7)
  const [stats, setStats] = useState<ActivityLogStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(false)

  const [listDays, setListDays] = useState<number>(30)
  const [actionFilter, setActionFilter] = useState<string>('')
  const [search, setSearch] = useState<string>('')
  const [searchInput, setSearchInput] = useState<string>('')
  const [events, setEvents] = useState<ActivityLogEvent[]>([])
  const [eventsLoading, setEventsLoading] = useState(false)

  const loadStats = useCallback(async (days: number) => {
    setStatsLoading(true)
    const result = await getActivityLogStats(days)
    setStats(result)
    setStatsLoading(false)
  }, [])

  const loadEvents = useCallback(
    async (days: number, action: string, q: string) => {
      setEventsLoading(true)
      const result = await getActivityLogs({
        days,
        action: action || undefined,
        q: q || undefined,
        limit: 200,
      })
      setEvents(result)
      setEventsLoading(false)
    },
    [],
  )

  useEffect(() => {
    loadStats(statsDays)
  }, [loadStats, statsDays])

  useEffect(() => {
    loadEvents(listDays, actionFilter, search)
  }, [loadEvents, listDays, actionFilter, search])

  // Feed a short screen-context hint to the chat assistant so it knows what
  // the user is looking at right now.
  useEffect(() => {
    const parts = [`User is viewing the Activity Logs dashboard.`]
    if (stats) {
      parts.push(
        `Last ${stats.window_days} days: ${stats.total_events} events,`,
        `${stats.active_users} active agency users,`,
        `${stats.active_candidates} active candidates.`,
      )
    }
    appStateContext?.dispatch({ type: 'SET_SCREEN_CONTEXT', payload: parts.join(' ') })
    return () => {
      appStateContext?.dispatch({ type: 'SET_SCREEN_CONTEXT', payload: '' })
    }
  }, [appStateContext, stats])

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput.trim())
  }

  const headlineCards = useMemo(() => {
    const by = stats?.by_action || {}
    const get = (action: string) => by[action] || 0
    return [
      {
        label: 'Agency logins',
        value: get('login'),
        sub: `${get('login_failed')} failed`,
      },
      {
        label: 'Candidate logins',
        value: get('candidate_login'),
        sub: `${get('candidate_login_failed')} failed`,
      },
      {
        label: 'Candidate registrations',
        value: get('candidate_register'),
        sub: `${get('candidate_cv_upload')} resumes uploaded`,
      },
      {
        label: 'Active agency users',
        value: stats?.active_users || 0,
        sub: `${stats?.active_candidates || 0} candidates active`,
      },
    ]
  }, [stats])

  const downloadReport = () => {
    const url = activityLogReportUrl({
      days: listDays,
      action: actionFilter || undefined,
      q: search || undefined,
    })
    window.open(url, '_blank')
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Activity Logs</h2>
        <span className={styles.subtitle}>
          Audit trail of logins, registrations, resume uploads and other account events
        </span>
      </div>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Overview</h3>
          <div className={styles.toolbar}>
            {WINDOW_OPTIONS.map(opt => (
              <button
                key={opt.days}
                type="button"
                className={`${styles.chip} ${statsDays === opt.days ? styles.chipActive : ''}`}
                onClick={() => setStatsDays(opt.days)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {statsLoading ? (
          <div className={styles.loading}><Spinner size={SpinnerSize.large} /></div>
        ) : stats ? (
          <>
            <div className={styles.cards}>
              {headlineCards.map(c => (
                <div key={c.label} className={styles.card}>
                  <div className={styles.cardLabel}>{c.label}</div>
                  <div className={styles.cardValue}>{c.value}</div>
                  <div className={styles.cardSub}>{c.sub}</div>
                </div>
              ))}
            </div>

            <div className={styles.splitRow}>
              <div className={styles.panel}>
                <div className={styles.panelTitle}>Events per day</div>
                {stats.daily.length === 0 ? (
                  <div className={styles.emptyPanel}>No events in window</div>
                ) : (
                  <div className={styles.barRow}>
                    {stats.daily.map(d => {
                      const max = Math.max(...stats.daily.map(x => x.count), 1)
                      const pct = Math.max(6, Math.round((d.count / max) * 100))
                      return (
                        <div key={d.day} className={styles.bar} title={`${d.day}: ${d.count}`}>
                          <div className={styles.barFillWrap}>
                            <div className={styles.barFill} style={{ height: `${pct}%` }} />
                          </div>
                          <div className={styles.barLabel}>{d.day.slice(5)}</div>
                          <div className={styles.barValue}>{d.count}</div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              <div className={styles.panel}>
                <div className={styles.panelTitle}>Top agency users</div>
                {stats.top_users.length === 0 ? (
                  <div className={styles.emptyPanel}>No user activity</div>
                ) : (
                  <table className={styles.miniTable}>
                    <tbody>
                      {stats.top_users.map((u, i) => (
                        <tr key={`${u.username}-${i}`}>
                          <td>{u.full_name || u.username || '—'}</td>
                          <td className={styles.numCell}>{u.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className={styles.emptyState}>Could not load stats.</div>
        )}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Events</h3>
          <div className={styles.toolbar}>
            <select
              className={styles.select}
              value={listDays}
              onChange={e => setListDays(parseInt(e.target.value, 10))}
            >
              {WINDOW_OPTIONS.map(opt => (
                <option key={opt.days} value={opt.days}>
                  Last {opt.label}
                </option>
              ))}
            </select>
            <select
              className={styles.select}
              value={actionFilter}
              onChange={e => setActionFilter(e.target.value)}
            >
              {ACTION_FILTERS.map(opt => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <form className={styles.searchForm} onSubmit={handleSearchSubmit}>
              <input
                className={styles.searchInput}
                type="text"
                placeholder="Search user or email…"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
              <DefaultButton type="submit" text="Search" />
            </form>
            <PrimaryButton
              iconProps={{ iconName: 'Download' }}
              text="Export CSV"
              onClick={downloadReport}
            />
          </div>
        </div>

        {eventsLoading ? (
          <div className={styles.loading}><Spinner size={SpinnerSize.large} /></div>
        ) : events.length === 0 ? (
          <div className={styles.emptyState}>No events match the current filter.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action</th>
                  <th>User / Candidate</th>
                  <th>Email</th>
                  <th>Entity</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {events.map(ev => {
                  const who = ev.full_name || ev.username
                    || (ev.candidateid ? `Candidate #${ev.candidateid}` : '—')
                  const entity = ev.entity_type
                    ? `${ev.entity_type}${ev.entity_id ? ` #${ev.entity_id}` : ''}`
                    : '—'
                  return (
                    <tr key={ev.logid}>
                      <td className={styles.tsCell}>{formatTimestamp(ev.timestamp)}</td>
                      <td>
                        <span className={`${styles.badge} ${actionBadgeClass(ev.action)}`}>
                          {actionLabel(ev.action)}
                        </span>
                      </td>
                      <td>{who}</td>
                      <td>{ev.detail_email || '—'}</td>
                      <td>{entity}</td>
                      <td>{ev.ip_address || '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

export default ActivityLogs
